/**
 * InsightKpiCard
 * 
 * KPI card for displaying key metrics and performance indicators in the agent chat.
 * Supports different "tones" (positive, warning, critical) for visual signaling.
 */
import { useNavigate } from 'react-router-dom';
import type { InsightAction, InsightKpiCardData } from '../types';

interface InsightKpiCardProps {
  data: InsightKpiCardData;
  onSelect: (selection: Record<string, unknown>) => void;
}

function toneClassName(tone?: string) {
  switch (tone) {
    case 'positive':
      return 'border-emerald-200 bg-emerald-50 text-emerald-900';
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-900';
    case 'critical':
      return 'border-rose-200 bg-rose-50 text-rose-900';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-900';
  }
}

export function InsightKpiCard({ data, onSelect }: InsightKpiCardProps) {
  const navigate = useNavigate();

  const handleAction = (action: InsightAction) => {
    if (action.destination) {
      navigate(action.destination);
    }
    onSelect({
      action_id: action.action_id,
      destination: action.destination,
    });
  };

  return (
    <div className="my-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-3 text-white">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Data Insight</p>
        <h3 className="mt-1 text-sm font-semibold">{data.title}</h3>
        {data.subtitle && <p className="mt-1 text-xs text-slate-300">{data.subtitle}</p>}
      </div>

      <div className="grid gap-2 p-3 sm:grid-cols-2">
        {data.metrics.map((metric) => (
          <div key={metric.id} className={`rounded-2xl border p-3 ${toneClassName(metric.tone)}`}>
            <p className="text-[11px] uppercase tracking-wide opacity-75">{metric.label}</p>
            <p className="mt-2 text-xl font-semibold">{metric.value}</p>
            {metric.change_text && <p className="mt-1 text-xs opacity-80">{metric.change_text}</p>}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-slate-100 px-3 py-3">
        {(data.actions ?? []).map((action) => (
          <button
            key={action.action_id}
            type="button"
            onClick={() => handleAction(action)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
              action.variant === 'secondary'
                ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {action.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSelect({ action_id: 'dismiss' })}
          className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
